// Tiny DOM builders (§15.1: textContent only — innerHTML is lint-banned). Shared by the sign-in/teams/
// account flows and the lesson overlays. No framework — the account UI is light enough for hand-built DOM.
//
// D157 (brand slice 1): retokened to the theme.css palette so overlays opened from the new home don't
// clash — the interactive accent is amber now (green stops meaning both "button" and "success"), text is
// the ink ramp, panels are --bg-panel with hairline strokes. Coherence only: the full redesigns of these
// flows are their own handoffs.

export function el<K extends keyof HTMLElementTagNameMap>(tag: K, style = '', text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (style) node.style.cssText = style;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function div(style = '', text?: string): HTMLDivElement {
  return el('div', style, text);
}

export function button(label: string, onClick: () => void, opts: { primary?: boolean; active?: boolean } = {}): HTMLButtonElement {
  const b = el('button', '', label);
  const border = opts.primary || opts.active ? 'var(--accent)' : 'var(--border-strong)';
  const bg = opts.primary ? 'var(--accent)' : opts.active ? 'var(--bg-raised)' : 'var(--bg-control)';
  const ink = opts.primary ? 'var(--on-accent)' : 'var(--ink-1)';
  b.style.cssText = `margin:3px;padding:${opts.primary ? '10px 20px' : '7px 14px'};font-size:${opts.primary ? '16px' : '14px'};font-weight:600;border:1px solid ${border};background:${bg};color:${ink};border-radius:8px;cursor:pointer;font-family:inherit`;
  b.onclick = onClick;
  return b;
}

export function textInput(placeholder: string, value = '', type = 'text'): HTMLInputElement {
  const i = el('input');
  i.type = type;
  i.placeholder = placeholder;
  i.value = value;
  i.style.cssText = 'margin:4px 0;padding:8px 10px;width:100%;box-sizing:border-box;border:1px solid var(--border-strong);background:var(--bg-keycap);color:var(--ink-1);border-radius:6px;font-family:inherit;font-size:14px';
  return i;
}

export function note(text: string, color = 'var(--ink-2)'): HTMLDivElement {
  return div(`margin:8px 0;font-size:13px;color:${color}`, text);
}

/** A centred full-screen overlay panel container (returns {overlay, mount}). Modal scrim level. */
export function overlayPanel(): { overlay: HTMLDivElement; mount: (content: HTMLElement) => void } {
  const overlay = div('position:fixed;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(4,22,12,.85);color:var(--ink-1);font-family:var(--font-ui);z-index:120;overflow:auto;padding:24px');
  const panel = div('min-width:340px;max-width:560px;width:100%;padding:24px 28px;border:1px solid var(--border-hairline);border-radius:12px;background:var(--bg-panel)');
  overlay.append(panel);
  return {
    overlay,
    mount: (content) => panel.replaceChildren(content),
  };
}
